import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

/**
 * Privacy Policy — Mythicmatrix.
 *
 * NOTE: This is a plain-language starter policy drafted for Google AdSense
 * approval and general transparency. It is NOT legal advice. Before launch
 * in regulated jurisdictions (EU/UK → GDPR, California → CCPA/CPRA, children's
 * data → COPPA), have a qualified attorney review it.
 */
export default function Privacy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated="February 8, 2026" testid="privacy-page">
      <p>
        Mythicmatrix ("we", "us", "the Vault") is an AI-driven roleplaying
        platform. This policy explains what information we collect from you,
        why we collect it, who sees it, and the choices you have.
      </p>

      <LegalSection title="1. Who this applies to">
        <p>
          This policy covers anyone who visits mythicmatrix.app (or any
          Mythicmatrix-operated domain), signs in, plays an adventure, publishes
          a custom world, or purchases a subscription. Mythicmatrix is intended
          for players aged 13 and older. If you are under 13, do not create an
          account or submit any information.
        </p>
      </LegalSection>

      <LegalSection title="2. What we collect">
        <LegalList>
          <li>
            <strong className="text-stone-100">Account data</strong> — your email
            address, display name, and the authentication provider you used
            (Google sign-in or email/password). Passwords are stored only as
            salted bcrypt hashes; we cannot recover them.
          </li>
          <li>
            <strong className="text-stone-100">Chronicle data</strong> — the
            adventures you start, choices you take, custom worlds you author,
            your character sheet, your Mythodex, and your inventory. This
            content is stored in our database to let you resume play.
          </li>
          <li>
            <strong className="text-stone-100">Payment data</strong> — if you
            subscribe to Sovereign or claim a Founding Sovereign seat, Stripe
            processes your payment. We receive back your Stripe customer ID,
            subscription tier, and payment status; we never see your full card
            number.
          </li>
          <li>
            <strong className="text-stone-100">Session cookies</strong> — we set
            an httpOnly, same-site cookie called <code>session_token</code> to
            keep you signed in for 7 days. No tracking cookies are set by us.
          </li>
          <li>
            <strong className="text-stone-100">Technical logs</strong> — standard
            server logs (IP address, user-agent, timestamp of request) retained
            for up to 30 days for abuse prevention and debugging.
          </li>
        </LegalList>
      </LegalSection>

      <LegalSection title="3. AI processing of your input">
        <p>
          When you take an action in a chronicle, your action text and relevant
          game state are sent to our third-party language-model provider
          (Anthropic Claude, routed through Emergent) to generate the narrative
          response. By design, our provider does not retain inputs for model
          training in our mode of use. Do not paste personal, sensitive, or
          confidential information into chronicles — the AI treats your text as
          creative input.
        </p>
      </LegalSection>

      <LegalSection title="4. Advertising">
        <p>
          Mythicmatrix uses <strong className="text-stone-100">Google AdSense</strong> to
          serve ads to non-Sovereign (free-tier) users. Sovereigns and Founding
          Sovereigns never see ads. Google and its partners may use cookies
          (DART cookie and similar) to serve ads based on your prior visits to
          this and other sites. You can opt out of personalized advertising at{" "}
          <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" className="gold underline">
            google.com/settings/ads
          </a>{" "}
          or, for third-party vendors generally, at{" "}
          <a href="https://www.aboutads.info" target="_blank" rel="noreferrer" className="gold underline">
            aboutads.info
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="5. Who we share data with">
        <p>We share your data only with the processors who make this app work:</p>
        <LegalList>
          <li>Google (sign-in verification, AdSense)</li>
          <li>Anthropic / Emergent (AI model inference for the GM narrator)</li>
          <li>Stripe (payment processing and subscription billing)</li>
          <li>MongoDB Atlas or the equivalent database host (data at rest)</li>
        </LegalList>
        <p>
          We do not sell your personal data. We do not share it with any party
          other than those necessary to deliver the service, respond to a lawful
          legal request, or protect our rights.
        </p>
      </LegalSection>

      <LegalSection title="6. Your choices and rights">
        <LegalList>
          <li>
            <strong className="text-stone-100">Access & delete</strong> — email
            <code> privacy@mythicmatrix.app</code> and we will export or delete
            your data within 30 days. Deleting your account removes your profile,
            all adventures, and all authored custom worlds (published worlds may
            be anonymized rather than deleted if other players have played them).
          </li>
          <li>
            <strong className="text-stone-100">Cancel subscription</strong> — in
            the Sovereign billing portal you can cancel at any time; access
            continues through the end of the paid period.
          </li>
          <li>
            <strong className="text-stone-100">Opt out of ads</strong> — see
            Section 4 above. Alternatively, ascend to Sovereign and the ads
            vanish entirely.
          </li>
        </LegalList>
        <p>
          If you reside in the EU/UK (GDPR) or California (CCPA/CPRA), you have
          additional rights to object, restrict, and port your data. Contact us
          at <code>privacy@mythicmatrix.app</code> to exercise them.
        </p>
      </LegalSection>

      <LegalSection title="7. Children">
        <p>
          Mythicmatrix is not directed to children under 13 and does not
          knowingly collect data from them. If you learn that a child under 13
          has given us information, email <code>privacy@mythicmatrix.app</code> and
          we will delete it.
        </p>
      </LegalSection>

      <LegalSection title="8. Security">
        <p>
          We use industry-standard safeguards: TLS everywhere, httpOnly cookies,
          bcrypt-hashed passwords, per-user data scoping on every database query,
          and strict rate limits on authentication and payment endpoints. No
          system is perfectly secure; if we detect a breach involving your data
          we will notify you promptly as required by law.
        </p>
      </LegalSection>

      <LegalSection title="9. Changes to this policy">
        <p>
          If we materially change this policy we will update the "last updated"
          date above and, where required, notify you by email or an in-app
          notice before the change takes effect.
        </p>
      </LegalSection>

      <LegalSection title="10. Contact">
        <p>
          Questions about this policy or your data: <code>privacy@mythicmatrix.app</code>.
        </p>
      </LegalSection>
    </LegalShell>
  );
}
